export type Day = {
  index?: number,
  title: string,
  shortTitle: string,
  enabled: boolean,
  startHours: string,
  startMinutes: string,
  endHours: string,
  endMinutes: string,
}

export const DAYS: Day[] = [
  {
    title: 'Monday',
    shortTitle: 'mon',
    enabled: true,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Tuesday',
    shortTitle: 'tue',
    enabled: true,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Wednesday',
    shortTitle: 'wed',
    enabled: true,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Thursday',
    shortTitle: 'thu',
    enabled: true,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Friday',
    shortTitle: 'fri',
    enabled: true,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Saturday',
    shortTitle: 'sat',
    enabled: false,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
  {
    title: 'Sunday',
    shortTitle: 'sun',
    enabled: false,
    startHours: '09',
    startMinutes: '00',
    endHours: '18',
    endMinutes: '00',
  },
];

export const validateWorkingHours = (days: Day[]) => days
  ?.reduce((prev: boolean, day: Day) => (!day.enabled || (day.startHours < day.endHours) || (
    (day.startHours === day.endHours) && day.startMinutes < day.endMinutes
  )) && prev, true);

export const validateWorkingDays = (days: Day[]) => days
  ?.filter((day: Day) => day.enabled).length > 0;

export const pad = (value: number) => (value < 10
  ? `0${Math.floor(value)}` : `${Math.floor(value)}`);

export const constrainHours = (value: string) => {
  const numeric: number = parseInt(value, 10);
  if (!numeric || numeric < 0) return 0;
  if (numeric > 23) return 23;
  return numeric;
};

export const constrainMinutes = (value: string) => {
  const numeric: number = parseInt(value, 10);
  if (!numeric || numeric < 0) return 0;
  if (numeric > 59) return 59;
  return numeric;
};

export type BuildingSettingsPriority = {
  Type: string,
  Name: string,
  ResponseHours: number,
  ResponseMinutes: number,
  ResolutionHours: number,
  ResolutionMinutes: number,
  Description: string,
}

export const BUILDING_SETTINGS_PRIORITIES: BuildingSettingsPriority[] = [
  {
    Type: 'P1',
    Name: 'Critical',
    ResponseHours: 0,
    ResponseMinutes: 30,
    ResolutionHours: 4,
    ResolutionMinutes: 0,
    Description: 'Something dangerous, life is at risk or multiple systems down',
  },
  {
    Type: 'P2',
    Name: 'High',
    ResponseHours: 1,
    ResponseMinutes: 0,
    ResolutionHours: 8,
    ResolutionMinutes: 0,
    Description: 'Someone can\'t work',
  },
  {
    Type: 'P3',
    Name: 'Moderate',
    ResponseHours: 4,
    ResponseMinutes: 0,
    ResolutionHours: 24,
    ResolutionMinutes: 0,
    Description: 'Annoyance: Example - flickering light',
  },
  {
    Type: 'P4',
    Name: 'Low',
    ResponseHours: 8,
    ResponseMinutes: 0,
    ResolutionHours: 40,
    ResolutionMinutes: 0,
    Description: 'Maintenance issue: damaged paint work etc.',
  },
  {
    Type: 'P5',
    Name: 'Other',
    ResponseHours: 24,
    ResponseMinutes: 0,
    ResolutionHours: 160,
    ResolutionMinutes: 0,
    Description: 'Other',
  },
];

export const convertHours = (text: string) => {
  const numeric: number = parseInt(text, 10);
  if (!numeric || numeric < 0) return 0;
  return numeric;
};

export const checkResponseTimeMinimum = (priority: BuildingSettingsPriority) => priority
  .ResponseHours > 0 || (priority.ResponseHours === 0 && priority.ResponseMinutes > 0);

export const checkResolutionTimeMinimum = (priority: BuildingSettingsPriority) => priority
  .ResolutionHours > 0 || (priority.ResolutionHours === 0 && priority.ResolutionMinutes > 0);

export const checkResponseTimeMaximum = (priority: BuildingSettingsPriority) => priority
  .ResponseHours < 720 || (priority.ResponseHours === 720 && priority.ResponseMinutes === 0);

export const checkResolutionTimeMaximum = (priority: BuildingSettingsPriority) => priority
  .ResolutionHours < 720 || (priority.ResolutionHours === 720 && priority.ResolutionMinutes === 0);

export const validatePriorityTimes = (priorities: BuildingSettingsPriority[]) => priorities
  ?.reduce((prev: boolean, priority: BuildingSettingsPriority) => (prev
    && ((priority.ResponseHours < priority.ResolutionHours)
      || (priority.ResponseHours === priority.ResolutionHours
        && (priority.ResponseMinutes < priority.ResolutionMinutes)))), true);

export const validateResponseTimes = (priorities: BuildingSettingsPriority[]) => priorities
  ?.reduce((prev: boolean, priority: BuildingSettingsPriority, index: number) => prev
    && (index > 0 ? ((priority.ResponseHours > priorities[index - 1].ResponseHours)
      || (priority.ResponseHours === priorities[index - 1].ResponseHours
        && (priority.ResponseMinutes >= priorities[index - 1].ResponseMinutes))) : true), true);

export const validateResolutionTimes = (priorities: BuildingSettingsPriority[]) => priorities
  ?.reduce((prev: boolean, priority: BuildingSettingsPriority, index: number) => prev
    && (index > 0 ? ((priority.ResolutionHours > priorities[index - 1].ResolutionHours)
      || (priority.ResolutionHours === priorities[index - 1].ResolutionHours
        && (priority.ResolutionMinutes >= priorities[index - 1].ResolutionMinutes))) : true), true);

export const validatePriorityNames = (priorities: BuildingSettingsPriority[]) => priorities
  ?.filter((priority: BuildingSettingsPriority) => priority.Name?.length > 0)
  .length > 0;

export const validatePriorityDescriptions = (priorities: BuildingSettingsPriority[]) => priorities
  ?.filter((priority: BuildingSettingsPriority) => priority.Description?.length > 0)
  .length > 0;
